import { Skeleton, Stack, Typography } from "@mui/material";
import { Dialog } from "../../components/dialogs/Dialog";
import { useAppQuery } from "../../hooks/useAppQuery";
import {
  ICompetencyCategory,
  ICompetencyDetailsResponse,
  ICompetencyTraining
} from "../../interfaces/Competency";
import { useLocale } from "../../providers/LocaleProvider";

const ImportCompetenciesDialog = (props: any) => {
  const {
    theme,
    onClose,
    open = false,
    competencyId,
    onImport = () => {},
    isImporting = false,
    isImport = true
  } = props;

  const { translate } = useLocale();

  const { data: competency, isLoading: isLoadingCompetency } =
    useAppQuery<ICompetencyDetailsResponse>(`/StandardLibrary/competencies/${competencyId}`, {
      enabled: !!competencyId
    });

  const data = competency?.standardLibraryCompetency;

  const skeletonLabels = [
    { key: "competencyName", label: translate("dialogs.competencyName") },
    { key: "description", label: translate("common.description") },
    { key: "competencyCategories", label: translate("dialogs.competencyCategories") },
    { key: "training", label: translate("entities.training") },
    { key: "expiry", label: translate("dialogs.expires") }
  ];

  const renderSkeleton = () => (
    <Stack spacing={2}>
      {skeletonLabels.map((item) => (
        <div key={item.key}>
          <Typography variant='subtitle2' color='text.secondary'>
            {item.label}
          </Typography>
          <Skeleton
            variant='text'
            width={item.key === "expiry" ? "10%" : "60%"}
            height={28}
            sx={{ mt: 0.5 }}
          />
        </div>
      ))}
    </Stack>
  );

  return (
    <Dialog
      open={open}
      theme={theme}
      onClose={onClose}
      fullWidth={true}
      maxWidth='sm'
      title={translate("dialogs.competencyDetails")}
      actions={[
        ...(isImport
          ? [
              {
                label: translate("dialogs.importCompetency"),
                primary: true,
                variant: "filled",
                disabled: isLoadingCompetency || isImporting,
                onClick: () => onImport(competencyId),
                isLoading: isImporting
              }
            ]
          : []),
        {
          label: translate("common.cancel"),
          onClick: onClose
        }
      ]}
    >
      {isLoadingCompetency ? (
        renderSkeleton()
      ) : (
        <Stack spacing={2}>
          <div>
            <Typography variant='subtitle2' color='text.secondary'>
              {translate("dialogs.competencyName")}
            </Typography>
            <Typography>{data?.name}</Typography>
          </div>

          <div>
            <Typography variant='subtitle2' color='text.secondary'>
              {translate("common.description")}
            </Typography>
            <Typography>
              {data?.description ? data?.description : translate("dialogs.noDescription")}
            </Typography>
          </div>

          {data?.competencyCategories && data.competencyCategories.length > 0 && (
            <div>
              <Typography variant='subtitle2' color='text.secondary'>
                {translate("dialogs.competencyCategories")}
              </Typography>
              <Typography>
                {data.competencyCategories.map((category: ICompetencyCategory) => (
                  <Typography key={category.id}>{category.name}</Typography>
                ))}
              </Typography>
            </div>
          )}

          {data?.trainings && data.trainings.length > 0 && (
            <div>
              <Typography variant='subtitle2' color='text.secondary'>
                {translate("entities.training")}
              </Typography>
              <Typography>
                {data.trainings.map((training: ICompetencyTraining) => (
                  <Typography key={training.id}>{training.courseName}</Typography>
                ))}
              </Typography>
            </div>
          )}

          <div>
            <Typography variant='subtitle2' color='text.secondary'>
              {translate("dialogs.expires")}
            </Typography>
            <Typography>
              {data?.hasExpiryDate ? translate("common.yes") : translate("common.no")}
            </Typography>
          </div>
        </Stack>
      )}
    </Dialog>
  );
};

export default ImportCompetenciesDialog;
